import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ArchiveIcon } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tender } from "@/graphql/__generated__/graphql";

/**
 * ArchivedAlertComponent displays an alert when a tender is archived
 * Shows different messages and actions based on whether the user is the tender owner
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isTenderOwner - Whether the current user is the tender owner
 * @param {Tender} props.tenderData - The tender data
 * @returns {JSX.Element} The archived alert component
 */
function ArchivedAlertComponent({ isTenderOwner, tenderData }: { isTenderOwner: boolean, tenderData: Tender }) {
  const { t } = useTranslation(["translation"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  const { archived, archiveReason } = tenderData;

  if (!archived) {
    return null;
  }

  return (
    <Alert>
      <ArchiveIcon className="h-4 w-4" />
      <AlertTitle>{t("edittender.details.archivedalert.title")}</AlertTitle>
      <AlertDescription>
        {t("edittender.details.archivedalert.description")}
        {isTenderOwner ? (
          <div>
            {t("edittender.details.archivedalert.unarchiveowner")}
            <Link
              to={`/tender/${tenderID}/options`}
              className="font-medium underline hover:no-underline ml-1"
            >
              {t("edittender.details.archivedalert.gotosettings")}
            </Link>
            .
          </div>
        ) : (
          <> {t("edittender.details.archivedalert.contactowner")}</>
        )}

        <div className="mt-4 w-full">
          <p className="text-sm font-medium mb-1">
            {t("edittender.details.archivedalert.archivedreason")}
          </p>
          <div className="bg-muted px-3 py-2 rounded-md">
            {archiveReason}
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
}

export default ArchivedAlertComponent;
